/**
 * Pure module-entitlement computation, deliberately WITHOUT `server-only`.
 *
 * `tenant-modules.ts` carries `import 'server-only'` because it reads the
 * database. The computation itself reads nothing, and it has a second caller
 * that cannot take that marker: the templated-email module gate is reachable
 * from `queries.ts`, which the client dashboard shell imports, and webpack
 * walks that graph through dynamic imports too. Splitting the pure half here
 * lets the email gate share the exact resolver `requireModule` uses rather
 * than growing a second, drifting copy of it.
 *
 * `tenant-modules.ts` re-exports both symbols, so existing importers are
 * unaffected and there is still one definition.
 */

import {
  type ModuleOrgType,
  type TenantModuleActivationSummary,
  computeTenantEnabledModules,
  resolveEffectiveBillingPlan,
} from '../billing';
import { normaliseOrgType } from '../plans';
import { isComplianceTrialAccessScopeTenant } from '../compliance-trial-access';

/**
 * The exact `tenants` column set this computation needs. Exported so every
 * caller selects the same columns; a caller that selects fewer would silently
 * compute a different entitlement.
 */
export const TENANT_MODULE_COLUMNS =
  'billing_plan, subscription_tier, subscription_status, brand_config, selected_packages, is_demo';

/**
 * The tenant billing/config fields required to resolve enabled modules. This is
 * the exact column set `requireModule` reads from `tenants`.
 */
export type TenantModuleRow = {
  billing_plan: string | null;
  subscription_tier: string | null;
  subscription_status: string | null;
  is_demo?: boolean | null;
  brand_config: Record<string, unknown> | null;
  selected_packages: string[] | null;
};

/**
 * Resolve a tenant's enabled module paths from an already-fetched tenant row +
 * its module activations. This is the single source of the module computation
 * shared by `requireModule` (which fetches the row itself for its role and
 * subscription checks) and {@link getTenantEnabledModules}, so the two paths can
 * never drift. Returns `null` for enterprise (all modules).
 */
export function computeEnabledModulesFromTenantRow(
  tenant: TenantModuleRow,
  moduleActivations: TenantModuleActivationSummary[] | null,
  options?: { applyDemoAllAccess?: boolean },
): string[] | null {
  if (
    options?.applyDemoAllAccess !== false &&
    tenant.is_demo === true &&
    tenant.billing_plan !== 'home_care_compliance'
  )
    return null;

  const tier = tenant.subscription_tier as string | null;
  const brandConfig = (tenant.brand_config as Record<string, unknown>) ?? {};
  const plan = brandConfig.plan as string | undefined;
  const selectedPackages =
    (tenant.selected_packages as string[]) ?? (brandConfig.selectedPackages as string[]) ?? [];

  const isComplianceTrial = isComplianceTrialAccessScopeTenant({
    billingPlan: tenant.billing_plan,
    subscriptionStatus: tenant.subscription_status,
    isDemo: tenant.is_demo,
    selectedPackages,
    brandConfig,
  });

  // The exact trial quintuple outranks legacy tier/plan fields. Production
  // tenants have a non-null subscription_tier default, so resolving that first
  // would silently widen a compliance trial back to a paid package.
  const effectivePlan = isComplianceTrial
    ? null
    : resolveEffectiveBillingPlan({
        billingPlan: tenant.billing_plan as string | null,
        subscriptionTier: tier,
        legacyPlan: plan,
      });

  // Normalise orgType against the plan so mis-provisioned tenants still gate
  // correctly (BUG-26: HC plans stored as MIXED were rendering residential
  // content). If the stored value disagrees with the plan, derive from plan.
  const storedOrgType = brandConfig.orgType as string | undefined;
  const planKeyForOrgType = effectivePlan || plan || tier || '';
  const orgType = isComplianceTrial
    ? storedOrgType
    : planKeyForOrgType
      ? normaliseOrgType(planKeyForOrgType, storedOrgType as never)
      : storedOrgType;

  return computeTenantEnabledModules({
    billingPlan: effectivePlan,
    complianceTrial: isComplianceTrial,
    selectedPackages,
    orgType: (orgType as ModuleOrgType | undefined) ?? null,
    systemEnabledModules: (brandConfig.systemEnabledModules as string[]) ?? [],
    adminDisabledModules: (brandConfig.adminDisabledModules as string[]) ?? [],
    legacyEnabledModules: (brandConfig.enabledModules as string[]) ?? null,
    moduleActivations: moduleActivations ?? [],
    goLiveScope: brandConfig.visibleGoLiveScope,
  });
}

/**
 * Resolve a tenant's enabled module paths — the same computation `requireModule`
 * performs — from just a Supabase client + tenant id. Returns `null` for
 * enterprise (all modules). Throws {@link TenantModulesLookupError} when the
 * tenant row or its activations cannot be read.
 *
 * Intended for read-side callers that need module entitlement outside the
 * `requireModule` guard (e.g. module-derived feature-flag defaults). It performs
 * its own tenant read + activations RPC; the cost is one tenant read plus the
 * activation summary RPC.
 */
